/**
 * Bootstraps the storefront `X-API-KEY` for pages that live OUTSIDE
 * `BranchProvider`.
 *
 * Every `/v1/online-ordering/*` call needs the tenant's public API key, which
 * normally arrives when BranchProvider fetches the tenant config. The
 * location-pin page is deliberately outside that provider — it has no branch,
 * no cart and no signed-in customer — but it still has the tenant slug in its
 * own URL (`/{slug}/pin/{token}`), which is all the config endpoint needs.
 *
 * Failure opens the gate with a null key rather than leaving it closed:
 * `apiFetch` waits on `apiKeyReady` before any online-ordering call, so a
 * silent failure here would hang the page forever instead of showing it
 * something. With the gate open the call goes out keyless and comes back
 * 401, which the caller can render as a normal failure.
 */
import { configApi } from "@/api/services";
import { setApiKey, setTenantId } from "@/api/http";
import { resolveSlug } from "@/lib/resolveSlug";

let inFlight: Promise<boolean> | null = null;

export function ensureStorefrontApiKey(): Promise<boolean> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    const { slug } = resolveSlug();
    if (!slug) {
      setApiKey(null);
      setTenantId(null);
      return false;
    }
    try {
      const cfg = await configApi.get(slug);
      setApiKey(cfg.apiKey ?? null);
      setTenantId(cfg.tenantId ?? null);
      return Boolean(cfg.apiKey);
    } catch {
      setApiKey(null);
      setTenantId(null);
      return false;
    }
  })();
  return inFlight;
}

/** Test-only: forget the memoised bootstrap between cases. */
export function resetStorefrontApiKeyForTests(): void {
  inFlight = null;
}
